"use client";

import { useMemo, useState } from "react";
import { getSiteContent } from "@/content";
import { type Locale } from "@/lib/site";
import { FieldInput, FieldSelect } from "@/components/ui/StitchUI";

export function ShippingCalculator({ locale }: { locale: Locale }) {
  const t = getSiteContent(locale).pages.calculator;
  const [destination, setDestination] = useState<string>(t.defaults.destination);
  const [channel, setChannel] = useState<string>(t.defaults.channel);
  const [weight, setWeight] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [goodsType, setGoodsType] = useState<string>(t.defaults.goodsType);
  const [selectedServices, setSelectedServices] = useState<string[]>(() => [...t.defaults.selectedServices]);

  const result = useMemo(() => {
    const actualWeight = Math.max(Number(weight) || 0, 0);
    const volumeWeight = ((Number(length) || 0) * (Number(width) || 0) * (Number(height) || 0)) / 6000;
    const chargeableWeight = Math.max(actualWeight, volumeWeight, 0.5);
    const surcharge = t.goodsTypes.find((item) => item[1] === goodsType)?.[2] ?? 0;
    const serviceFee = t.services.filter((service) => selectedServices.includes(service.name)).reduce((sum, service) => sum + service.fee, 0);
    const selectedChannel = t.channels.find((item) => item.value === channel) ?? t.channels[0];
    const selectedDestinationFactor = t.destinations.find((item) => item[0] === destination)?.[1] ?? 1;
    const base = chargeableWeight * selectedChannel.rate * selectedDestinationFactor;
    const total = base + surcharge + serviceFee;
    return {
      chargeableWeight,
      base,
      surcharge,
      serviceFee,
      total
    };
  }, [channel, destination, goodsType, height, length, selectedServices, t.channels, t.destinations, t.goodsTypes, t.services, weight, width]);

  const selectedChannel = t.channels.find((item) => item.value === channel) ?? t.channels[0];
  const eta = selectedChannel.eta;
  const recommendedRoute = selectedChannel.route;

  function toggleService(name: string) {
    setSelectedServices((current) => (current.includes(name) ? current.filter((item) => item !== name) : [...current, name]));
  }

  return (
    <main className="mx-auto max-w-container-max px-margin-mobile pb-section-gap pt-[156px] md:px-margin-desktop md:pt-[168px]">
      <header className="mb-stack-lg">
        <h1 className="typo-page-h1 mb-stack-sm text-primary">{t.title}</h1>
        <p className="max-w-3xl font-body-md text-body-md text-on-surface-variant">{t.subtitle}</p>
      </header>

      <div className="grid grid-cols-1 items-start gap-gutter lg:grid-cols-12">
        <section className="relative rounded-card border border-border-pale bg-white p-stack-lg shadow-sm lg:col-span-7">
          <form className="space-y-stack-lg" onSubmit={(event) => event.preventDefault()}>
            <div className="grid grid-cols-1 gap-stack-md md:grid-cols-2">
              <SelectWithIcon icon="location_on" label={t.form.destinationLabel} placeholder={t.form.destinationPlaceholder} value={destination} onChange={setDestination} options={t.destinations.map((item) => item[0])} />
              <SelectWithIcon icon="local_shipping" label={t.form.channelLabel} value={channel} onChange={setChannel} options={t.channels.map((item) => item.value)} />
            </div>

            <div className="space-y-stack-md rounded-xl bg-surface-container-low p-stack-md">
              <div className="mb-2 flex items-center gap-2">
                <span className="material-symbols-outlined text-on-tertiary-container">box</span>
                <h2 className="font-label-md text-label-md font-bold text-primary">{t.form.packageTitle}</h2>
              </div>
              <div className="grid grid-cols-2 gap-stack-md md:grid-cols-4">
                <NumberField label={t.form.numberFields[0][0]} placeholder={t.form.numberFields[0][1]} value={weight} onChange={setWeight} />
                <NumberField label={t.form.numberFields[1][0]} placeholder={t.form.numberFields[1][1]} value={length} onChange={setLength} />
                <NumberField label={t.form.numberFields[2][0]} placeholder={t.form.numberFields[2][1]} value={width} onChange={setWidth} />
                <NumberField label={t.form.numberFields[3][0]} placeholder={t.form.numberFields[3][1]} value={height} onChange={setHeight} />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="px-1 font-label-md text-label-md text-primary">{t.form.goodsTypeLabel}</label>
              <div className="grid grid-cols-2 gap-stack-sm sm:grid-cols-3 md:grid-cols-5">
                {t.goodsTypes.map(([icon, label]) => (
                  <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-border-pale p-3 transition-colors has-[:checked]:border-secondary has-[:checked]:bg-secondary-fixed/30 hover:bg-surface-container-low" key={label}>
                    <input checked={goodsType === label} className="hidden" name="goods_type" onChange={() => setGoodsType(label)} type="radio" />
                    <span className="material-symbols-outlined text-sm text-secondary">{icon}</span>
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="px-1 font-label-md text-label-md text-primary">{t.form.servicesLabel}</label>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {t.services.map(({ name, copy }) => (
                  <label className="group flex cursor-pointer items-start gap-3 rounded-xl border border-border-pale p-4 transition-all hover:shadow-sm" key={name}>
                    <input
                      checked={selectedServices.includes(name)}
                      className="mt-1 h-5 w-5 rounded border-outline text-secondary focus:outline-none focus:ring-0"
                      onChange={() => toggleService(name)}
                      type="checkbox"
                    />
                    <div>
                      <p className="font-label-md text-label-md text-on-surface">{name}</p>
                      <p className="font-body-sm text-on-surface-variant">{copy}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <button className="flex w-full items-center justify-center gap-2 rounded-full bg-parcel-orange px-8 py-4 font-headline-md text-headline-md text-white shadow-lg shadow-parcel-orange/20 transition-all hover:brightness-110 active:scale-[0.98]" type="button">
              <span className="material-symbols-outlined">calculate</span>
              {t.form.submit}
            </button>
          </form>
        </section>

        <aside className="top-margin-desktop lg:sticky lg:col-span-5">
          <div className="ambient-deep-bg relative overflow-hidden rounded-card bg-primary p-0 text-white shadow-2xl">
            <div className="h-1 animate-flow opacity-60" />
            <div className="p-stack-lg">
              <div className="mb-stack-lg flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                <div>
                  <p className="font-label-md text-label-md uppercase tracking-widest text-glacier-white opacity-90">{t.result.title}</p>
                  <h2 className="calculator-total-amount flex items-baseline gap-1">
                    <span className="calculator-total-currency">¥</span>
                    <span>{result.total.toFixed(2)}</span>
                  </h2>
                </div>
                <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-md">
                  <span className="material-symbols-outlined text-sm text-secondary-fixed-dim">speed</span>
                  <span className="font-label-md">{eta}</span>
                </div>
              </div>

              <div className="mb-stack-lg grid grid-cols-2 gap-stack-md">
                <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <p className="mb-1 font-label-sm text-label-sm text-tertiary-fixed/70">{t.result.recommendedRoute}</p>
                  <p className="flex items-center gap-1 font-bold">
                    {recommendedRoute}
                    <span className="material-symbols-outlined text-xs text-on-tertiary-container">verified</span>
                  </p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <p className="mb-1 font-label-sm text-label-sm text-tertiary-fixed/70">{t.result.chargeableWeight}</p>
                  <p className="font-bold">{result.chargeableWeight.toFixed(2)} kg</p>
                </div>
              </div>

              <div className="space-y-4 border-t border-white/10 pt-stack-lg">
                <h3 className="flex items-center gap-2 font-label-md text-label-md text-white/80">
                  <span className="material-symbols-outlined text-sm">pie_chart</span>
                  {t.result.costTitle}
                </h3>
                <div className="space-y-stack-sm">
                  <FeeRow label={t.result.rows.base} value={result.base} />
                  <FeeRow label={t.result.rows.storage} value={0} success />
                  <FeeRow label={t.result.rows.services} value={result.serviceFee} />
                  <FeeRow label={t.result.rows.surcharge} value={result.surcharge} />
                </div>
              </div>

              <div className="mt-stack-lg flex gap-3 rounded-xl border border-white/10 bg-primary-container/50 p-4">
                <span className="material-symbols-outlined text-sm text-tertiary-fixed-dim">priority_high</span>
                <p className="font-body-sm text-glacier-white/90">{t.result.notice}</p>
              </div>
            </div>
          </div>

          <div className="group relative mt-gutter h-48 overflow-hidden rounded-card border border-border-pale">
            <div className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-110" style={{ backgroundImage: "url('/assets/images/cargo-plane.png')" }} />
            <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-primary/80 to-transparent p-stack-md">
              <p className="font-bold text-white">{t.result.visualTitle}</p>
              <p className="font-body-sm text-white/70">{t.result.visualCopy}</p>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

function SelectWithIcon({
  icon,
  label,
  placeholder,
  value,
  onChange,
  options
}: {
  icon: string;
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className="px-1 font-label-md text-label-md text-primary">{label}</label>
      <div className="relative group">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-secondary">{icon}</span>
        <FieldSelect className="shipping-select-with-icon cursor-pointer appearance-none bg-surface-container-lowest py-3 pl-10 pr-10 focus:border-secondary focus:ring-2 focus:ring-secondary/20" value={value} onChange={(event) => onChange(event.target.value)}>
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </FieldSelect>
        <span className="pointer-events-none material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-outline">expand_more</span>
      </div>
    </div>
  );
}

function NumberField({ label, placeholder, value, onChange }: { label: string; placeholder: string; value: string; onChange: (value: string) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="font-label-sm text-label-sm text-on-surface-variant">{label}</label>
      <FieldInput className="shipping-number-field rounded-lg bg-white p-3 focus:border-ocean-cyan focus:ring-1 focus:ring-ocean-cyan/20" min="0" placeholder={placeholder} type="number" value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function FeeRow({ label, value, success = false }: { label: string; value: number; success?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1 font-body-sm text-white/60">
        {label}
        {success ? <span className="material-symbols-outlined text-[14px] text-success">check_circle</span> : null}
      </span>
      <span className={`font-medium ${success ? "text-success" : ""}`}>¥ {value.toFixed(2)}</span>
    </div>
  );
}
